// Ordering for the Milestones tab on a SOW. The API returns milestones by their
// hidden sort_order, which nobody can edit and which drifts as milestones are
// added, split or imported, so the table sorts itself: by milestone code unless
// the person clicks another heading. Codes compare naturally (AVM-2 before
// AVM-10), statuses by their place in the lifecycle rather than alphabetically,
// and blank dates, amounts and names sink to the bottom in either direction so
// they never crowd out the rows that have a value.
import { ALL_MILESTONE_STATUSES } from './milestoneStatus.js';

export const DEFAULT_MILESTONE_SORT = { col: 'code', dir: 'asc' };

const text = (v) => (v == null ? '' : String(v).trim());
const naturalCompare = (a, b) => text(a).localeCompare(text(b), 'en-GB', { numeric: true, sensitivity: 'base' });

// Unknown and legacy statuses (Split, Credit Noted) sort after the known ones.
const statusRank = (s) => {
  const i = ALL_MILESTONE_STATUSES.findIndex(k => k.toLowerCase() === text(s).toLowerCase());
  return i === -1 ? ALL_MILESTONE_STATUSES.length : i;
};

// Each column's value for a milestone; null means blank. rollupLine is the
// framework roll-up for the row, present only on a framework SOW.
const VALUES = {
  code:       (m) => text(m.milestone_code) || null,
  status:     (m) => statusRank(m.status),
  contract:   (m) => m.contract_date || null,
  forecast:   (m) => m.forecast_date || null,
  description:(m) => text(m.description) || null,
  amount:     (m) => m.invoice_amount_excl ?? null,
  called_off: (m, line) => line ? line.called_off : null,
  invoiced:   (m, line) => line ? line.invoiced : null,
  left:       (m, line) => line ? (m.invoice_amount_excl || 0) - line.called_off : null,
  coupa:      (m) => text(m.coupa_approved) || null,
  pm:         (m) => text(m.pm_name) || null,
};

export const MILESTONE_SORT_COLUMNS = Object.keys(VALUES);

const compareValues = (a, b) =>
  typeof a === 'number' && typeof b === 'number' ? a - b : naturalCompare(a, b);

// Returns a new array; the input is left alone. Ties fall back to the code so
// the order is stable and predictable whichever column is chosen.
export function sortMilestones(milestones, { col, dir } = DEFAULT_MILESTONE_SORT, rollupLines = null) {
  const value = VALUES[col] || VALUES.code;
  const sign = dir === 'desc' ? -1 : 1;
  const line = (m) => (rollupLines ? rollupLines[m.id] || null : null);
  return [...(milestones || [])].sort((a, b) => {
    const av = value(a, line(a));
    const bv = value(b, line(b));
    if (av == null && bv != null) return 1;
    if (bv == null && av != null) return -1;
    const cmp = av == null ? 0 : compareValues(av, bv);
    return cmp !== 0 ? sign * cmp : naturalCompare(a.milestone_code, b.milestone_code);
  });
}

// Clicking the active heading flips its direction; clicking another starts it ascending.
export const nextMilestoneSort = (current, col) =>
  current.col === col ? { col, dir: current.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' };
